import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { IContest } from '../../../types/global/helper';
import type { IHost, IPartner } from '../../../redux/discovery/types';
import { UploadContestAvatarGroup } from '../../../components/EsForm/UploadContestAvatarGroup';
import { DrawerSectionEditorActions, editTriggerSx, sectionShellSx } from './BrandContestDetailsDrawerUi';
import { BrandDrawerEmptyMessage, brandChipSx, dashedFieldShellSx } from './brandContestDrawerFieldUtils';
import { editorialHeadlineSx, editorialLabelSx, midnight } from '../contestWizard/midnightStyles';

type EditSection = 'host' | 'partner' | null;

export interface BrandContestHostsPartnersDrawerBodyProps {
    contest: IContest;
    onSaveChanges: (payload: Record<string, unknown>) => void;
    saving?: boolean;
}

export function BrandContestHostsPartnersDrawerBody({
    contest,
    onSaveChanges,
    saving,
}: BrandContestHostsPartnersDrawerBodyProps) {
    const [editing, setEditing] = useState<EditSection>(null);
    const [draftHost, setDraftHost] = useState<IHost | null | undefined>(contest.host);
    const [draftPartners, setDraftPartners] = useState<IPartner[]>(contest.partner ?? []);

    useEffect(() => {
        if (editing !== null) return;
        setDraftHost(contest.host);
        setDraftPartners(contest.partner ?? []);
    }, [contest.host, contest.partner, editing]);

    const beginEdit = (section: Exclude<EditSection, null>) => {
        setDraftHost(contest.host);
        setDraftPartners(contest.partner ?? []);
        setEditing(section);
    };

    const cancelEdit = () => setEditing(null);

    const saveHost = useCallback(() => {
        onSaveChanges({ host: draftHost ?? null });
        setEditing(null);
    }, [draftHost, onSaveChanges]);

    const savePartners = useCallback(() => {
        onSaveChanges({ partner: draftPartners });
        setEditing(null);
    }, [draftPartners, onSaveChanges]);

    const sectionHeader = (title: string, section: Exclude<EditSection, null>) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 20, color: midnight.onSurface }}>{title}</Typography>
            {editing === section ? (
                <Box />
            ) : (
                <Button type="button" variant="text" size="small" onClick={() => beginEdit(section)} sx={editTriggerSx}>
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    Edit
                </Button>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={sectionShellSx}>
                {sectionHeader('Host', 'host')}
                <Typography sx={{ ...editorialLabelSx, mb: 2, textTransform: 'none', letterSpacing: '0.04em' }}>
                    Select the host of your contest
                </Typography>
                {editing === 'host' ? (
                    <Box>
                        <UploadContestAvatarGroup
                            key={`host-edit-${contest._id}-${contest.host?._id ?? 'none'}`}
                            value={draftHost ?? undefined}
                            title="Select Host"
                            searchType="host"
                            visualVariant="brand"
                            disableTitle={false}
                            isEditMode
                            onChange={selected => setDraftHost(selected)}
                        />
                        <DrawerSectionEditorActions onCancel={cancelEdit} onSave={saveHost} saving={saving} />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('host')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        {contest.host?.name ? (
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: midnight.onSurface }}>
                                {contest.host.name}
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AddIcon sx={{ fontSize: 18, color: midnight.primary }} />
                                <BrandDrawerEmptyMessage message="Click to add a host" />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Partners', 'partner')}
                <Typography sx={{ ...editorialLabelSx, mb: 2, textTransform: 'none', letterSpacing: '0.04em' }}>
                    Select the partners of your contest
                </Typography>
                {editing === 'partner' ? (
                    <Box>
                        <UploadContestAvatarGroup
                            key={`partners-edit-${contest._id}-${(contest.partner ?? []).length}`}
                            value={draftPartners}
                            title="Select Partner(s)"
                            searchType="partner"
                            visualVariant="brand"
                            disableTitle={false}
                            isEditMode
                            onChange={selected => setDraftPartners(selected ?? [])}
                        />
                        <DrawerSectionEditorActions onCancel={cancelEdit} onSave={savePartners} saving={saving} />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('partner')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        {contest.partner && contest.partner.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.partner.map((partner, index) => (
                                    <Box key={partner._id ?? index} component="span" sx={brandChipSx}>
                                        {partner.name}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AddIcon sx={{ fontSize: 18, color: midnight.primary }} />
                                <BrandDrawerEmptyMessage message="Click to add partners" />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
